import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, MessageSquare, Info, FileText, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ─── Groupe 1 : Soumission ───────────────────────────────────────────────────
const CATS_SOUMISSION = [
  { value: 'notes_soumission',   label: 'Notes Soumission',    color: 'bg-teal-100 text-teal-800' },
  { value: 'description_travaux', label: 'Description Travaux', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'termes_conditions',  label: 'Termes et Conditions', color: 'bg-indigo-100 text-indigo-800' },
];

// ─── Groupe 2 : Actions Plateforme (envois manuels) ──────────────────────────
const CATS_PLATEFORME = [
  { value: 'rendez_vous',          label: 'Rendez-vous',                    color: 'bg-blue-100 text-blue-800' },
  { value: 'soumission',           label: 'Courriel Soumission',            color: 'bg-green-100 text-green-800' },
  { value: 'presentation',         label: 'Planification Travaux',          color: 'bg-sky-100 text-sky-800' },
  { value: 'facture',              label: 'Facture',                        color: 'bg-purple-100 text-purple-800' },
  { value: 'suivi',                label: 'Suivi Client',                   color: 'bg-yellow-100 text-yellow-800' },
  { value: 'document_garantie',    label: 'Document — Garantie',            color: 'bg-amber-100 text-amber-800' },
  { value: 'document_location',    label: 'Document — Location Équipements', color: 'bg-orange-100 text-orange-800' },
  { value: 'document_contractuel', label: 'Document — Contractuel',         color: 'bg-rose-100 text-rose-800' },
  { value: 'autre',                label: 'Autre',                          color: 'bg-gray-100 text-gray-800' },
];

const ALL_CATS = [...CATS_SOUMISSION, ...CATS_PLATEFORME];
const ALL_CAT_VALUES = ALL_CATS.map(c => c.value);

const VARIABLES_PAR_CATEGORIE = {
  notes_soumission:    ['client_nom', 'projet', 'date', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  description_travaux: ['client_nom', 'adresse_travaux', 'type_travaux', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  termes_conditions:   ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  rendez_vous:         ['client_nom', 'date_rdv', 'heure_rdv', 'representant', 'telephone_representant', 'adresse_travaux', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  soumission:          ['client_nom', 'numero_soumission', 'montant', 'montant_depot', 'lien_soumission', 'avis_google', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'logo_email'],
  presentation:        ['client_nom', 'nom_dossier', 'adresse_travaux', 'dates_chantier', 'chef_equipe', 'telephone_chef_equipe', 'charge_projet', 'telephone_charge_projet', 'representant', 'telephone_representant', 'tableau_produits', 'lien_codes_couleur', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'avis_google', 'logo_email'],
  facture:             ['client_nom', 'numero_facture', 'montant_total', 'montant_du', 'date_echeance', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  suivi:               ['client_nom', 'representant', 'telephone_representant', 'nom_projet', 'jours_depuis_envoi', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  document_garantie:   ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'duree_garantie', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  document_location:   ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'equipement_nom', 'duree_location', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  document_contractuel: ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'date', 'numero_contrat', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  autre:               ['client_nom', 'nom_destinataire', 'date', 'nom_expediteur', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
};

// ─── Composant principal ─────────────────────────────────────────────────────
export function MessagesPreenregistres() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupe, setGroupe] = useState('soumission'); // 'soumission' | 'plateforme'
  const [filtreCategorie, setFiltreCategorie] = useState('tous');
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', categorie: 'notes_soumission', sujet: '', contenu: '',
    variables_disponibles: VARIABLES_PAR_CATEGORIE['notes_soumission'],
  });

  // Catégories actives selon le groupe sélectionné
  const activeCats = groupe === 'soumission' ? CATS_SOUMISSION : CATS_PLATEFORME;
  const activeCatValues = activeCats.map(c => c.value);

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Sync variables quand la catégorie change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      variables_disponibles: VARIABLES_PAR_CATEGORIE[prev.categorie] || [],
    }));
  }, [formData.categorie]);

  // Réinitialiser le filtre catégorie quand on change de groupe
  useEffect(() => {
    setFiltreCategorie('tous');
  }, [groupe]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages-templates`);
      if (res.status !== 200) throw new Error();
      const data = res.data;
      // Garder uniquement les templates qui appartiennent à cette page (soumission + plateforme)
      setTemplates((data.templates || []).filter(t => ALL_CAT_VALUES.includes(t.categorie)));
    } catch {
      toast.error('Erreur chargement des templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nom || !formData.sujet || !formData.contenu) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      const res = editingTemplate
        ? await axios.put(`${API_URL}/api/messages-templates/${editingTemplate.id}`, formData)
        : await axios.post(`${API_URL}/api/messages-templates`, formData);
      if (res.status !== 200) throw new Error();
      toast.success(editingTemplate ? 'Template modifié !' : 'Template créé !');
      setShowModal(false);
      fetchTemplates();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (template) => {
    // Changer de groupe si le template édité appartient à l'autre groupe
    const isPlat = CATS_PLATEFORME.map(c => c.value).includes(template.categorie);
    setGroupe(isPlat ? 'plateforme' : 'soumission');
    setEditingTemplate(template);
    setFormData({ ...template, variables_disponibles: VARIABLES_PAR_CATEGORIE[template.categorie] || [] });
    setShowModal(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/messages-templates/${templateId}`);
      if (res.status !== 200) throw new Error();
      toast.success('Template supprimé !');
      fetchTemplates();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openCreateModal = () => {
    const defaultCat = activeCats[0].value;
    setEditingTemplate(null);
    setFormData({
      nom: '', categorie: defaultCat, sujet: '', contenu: '',
      variables_disponibles: VARIABLES_PAR_CATEGORIE[defaultCat] || [],
    });
    setShowModal(true);
  };

  const insererVariable = (variable) => {
    setFormData(prev => ({ ...prev, contenu: prev.contenu + `{${variable}}` }));
  };

  // Templates filtrés : groupe actif + filtre catégorie
  const templatesFiltres = templates.filter(t => {
    if (!activeCatValues.includes(t.categorie)) return false;
    if (filtreCategorie !== 'tous' && t.categorie !== filtreCategorie) return false;
    return true;
  });

  const countByGroupe = (cats) =>
    templates.filter(t => cats.map(c => c.value).includes(t.categorie)).length;

  if (loading) {
    return (
      <div className="p-6 text-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 max-w-6xl mx-auto space-y-6">

      {/* En-tête */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Messages Templates</h1>
          <p className="text-gray-500 text-sm mt-1">
            Modèles de texte utilisés dans les soumissions et les envois manuels de la plateforme
          </p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 gap-2 w-full sm:w-auto"
          data-testid="btn-new-template"
        >
          <Plus className="w-4 h-4" /> Nouveau Template
        </Button>
      </div>

      {/* Sélecteur de groupe */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setGroupe('soumission')}
          data-testid="groupe-soumission"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            groupe === 'soumission'
              ? 'bg-white shadow text-slate-800'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileText className="w-4 h-4" />
          Soumission
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
            groupe === 'soumission' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-500'
          }`}>
            {countByGroupe(CATS_SOUMISSION)}
          </span>
        </button>
        <button
          onClick={() => setGroupe('plateforme')}
          data-testid="groupe-plateforme"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            groupe === 'plateforme'
              ? 'bg-white shadow text-slate-800'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Wrench className="w-4 h-4" />
          Actions Plateforme
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
            groupe === 'plateforme' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'
          }`}>
            {countByGroupe(CATS_PLATEFORME)}
          </span>
        </button>
      </div>

      {/* Description du groupe actif */}
      <div className={`text-xs px-4 py-2 rounded-lg border ${
        groupe === 'soumission'
          ? 'bg-teal-50 border-teal-200 text-teal-700'
          : 'bg-blue-50 border-blue-200 text-blue-700'
      }`}>
        {groupe === 'soumission'
          ? 'Ces templates apparaissent directement dans les sections de la soumission (notes, description des travaux, termes et conditions).'
          : 'Ces templates sont utilisés pour les envois manuels depuis la plateforme (confirmation de rendez-vous, courriel de soumission, facture, suivi client, etc.).'
        }
      </div>

      {/* Filtre par catégorie */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm shrink-0">Filtrer :</Label>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setFiltreCategorie('tous')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
              filtreCategorie === 'tous'
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}
          >
            Tous ({templatesFiltres.length})
          </button>
          {activeCats.map(cat => {
            const count = templates.filter(t => t.categorie === cat.value).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.value}
                onClick={() => setFiltreCategorie(cat.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                  filtreCategorie === cat.value
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
              >
                {cat.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste des templates */}
      {templatesFiltres.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Aucun template dans cette catégorie</p>
            <p className="text-gray-400 text-sm mt-1 mb-4">
              {groupe === 'soumission'
                ? 'Créez un template pour les sections de votre soumission.'
                : 'Créez un template pour vos envois manuels.'}
            </p>
            <Button onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" /> Créer un template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templatesFiltres.map((template) => {
            const catInfo = ALL_CATS.find(c => c.value === template.categorie);
            return (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <p className="font-bold text-base">{template.nom}</p>
                        <Badge className={catInfo?.color}>{catInfo?.label}</Badge>
                      </div>
                      {template.sujet && (
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Sujet :</strong> {template.sujet}
                        </p>
                      )}
                      <div className="p-3 bg-gray-50 rounded border border-gray-100">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
                          {template.contenu.replace(/<[^>]+>/g, ' ').substring(0, 220)}
                          {template.contenu.length > 220 ? '…' : ''}
                        </p>
                      </div>
                      {template.variables_disponibles?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {template.variables_disponibles.slice(0, 6).map(v => (
                            <Badge key={v} variant="outline" className="text-xs font-mono">{`{${v}}`}</Badge>
                          ))}
                          {template.variables_disponibles.length > 6 && (
                            <span className="text-xs text-gray-400">+{template.variables_disponibles.length - 6} autres</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(template)} title="Modifier">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(template.id)} title="Supprimer">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Créer / Modifier */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Modifier' : 'Créer'} un Template —{' '}
              <span className="text-blue-600 font-normal">
                {groupe === 'soumission' ? 'Soumission' : 'Actions Plateforme'}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom du Template <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Ex: Description Peinture Intérieure"
                  value={formData.nom}
                  onChange={e => setFormData(p => ({ ...p, nom: e.target.value }))}
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={val => setFormData(p => ({ ...p, categorie: val }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {activeCats.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Sujet <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Ex: Description des travaux de peinture"
                value={formData.sujet}
                onChange={e => setFormData(p => ({ ...p, sujet: e.target.value }))}
              />
            </div>
            <div>
              <Label>Contenu <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Contenu du template..."
                value={formData.contenu}
                onChange={e => setFormData(p => ({ ...p, contenu: e.target.value }))}
                rows={10}
              />
            </div>
            {formData.variables_disponibles?.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <div className="flex items-start gap-2 mb-3">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Variables disponibles</p>
                    <p className="text-xs text-blue-600">Cliquez pour insérer dans le contenu</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.variables_disponibles.map(variable => (
                    <Button
                      key={variable} type="button" size="sm" variant="outline"
                      className="text-xs font-mono" onClick={() => insererVariable(variable)}
                    >
                      {`{${variable}}`}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
              <Button onClick={handleSave}>{editingTemplate ? 'Enregistrer' : 'Créer'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
